import React, { useMemo, useState } from "react";
import { FORMULAS, roundTo, fmt, toNum } from "../lib/calc";
import { ArrowLeftRight, Target } from "lucide-react";

export const QuickCalculator = ({
  unit,
  oneRM,
  setOneRM,
  formula,
  setFormula,
  rounding,
  setRounding,
  exercise,
  setExercise,
  exercises,
}) => {
  const [mode, setMode] = useState("direct"); // 'direct' | 'estimate'
  const [liftWeight, setLiftWeight] = useState(225);
  const [liftReps, setLiftReps] = useState(5);

  const estimated1RM = useMemo(() => {
    const fn = FORMULAS[formula].fn;
    return fn(toNum(liftWeight), toNum(liftReps));
  }, [liftWeight, liftReps, formula]);

  const commitEstimate = () => {
    setOneRM(roundTo(estimated1RM, rounding));
  };

  return (
    <div
      data-testid="quick-calculator"
      className="tactical-card p-5 sm:p-6 flex flex-col gap-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">Entrée · Athlète</div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase text-white leading-none mt-1">
            Calculateur de charge
          </h2>
        </div>
        <div
          className="flex rounded-sm border border-white/10 text-[11px] font-mono uppercase tracking-widest"
          data-testid="mode-toggle"
        >
          <button
            data-testid="mode-direct"
            onClick={() => setMode("direct")}
            className={`px-3 py-1.5 transition ${
              mode === "direct"
                ? "bg-white text-black"
                : "text-slate-400 hover:text-white"
            }`}
          >
            1RM direct
          </button>
          <button
            data-testid="mode-estimate"
            onClick={() => setMode("estimate")}
            className={`px-3 py-1.5 transition ${
              mode === "estimate"
                ? "bg-white text-black"
                : "text-slate-400 hover:text-white"
            }`}
          >
            Estimer par set
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="eyebrow text-[10px]">Exercice</span>
          <select
            data-testid="exercise-select"
            value={exercise}
            onChange={(e) => setExercise(e.target.value)}
            className="bg-[#12141a] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white focus:border-[#cd383d] outline-none"
          >
            {exercises.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="eyebrow text-[10px]">Arrondi</span>
          <select
            data-testid="rounding-select"
            value={rounding}
            onChange={(e) => setRounding(Number(e.target.value))}
            className="bg-[#12141a] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white focus:border-[#cd383d] outline-none"
          >
            <option value={0}>Exact</option>
            <option value={2.5}>2,5 {unit}</option>
            <option value={5}>5 {unit}</option>
          </select>
        </label>
      </div>

      {mode === "direct" ? (
        <div className="grid grid-cols-1 gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="eyebrow text-[10px]">Charge maximale (1RM)</span>
            <div className="flex items-stretch">
              <input
                data-testid="input-onerepmax"
                type="number"
                min="0"
                step="0.5"
                value={oneRM}
                onChange={(e) =>
                  setOneRM(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="w-full bg-[#12141a] border border-white/10 rounded-l-sm px-3 py-3 text-2xl font-mono font-bold text-white focus:border-[#cd383d] outline-none tabular"
              />
              <span className="flex items-center px-4 bg-[#1f232d] border border-l-0 border-white/10 rounded-r-sm text-sm font-mono text-slate-400 uppercase">
                {unit}
              </span>
            </div>
          </label>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="eyebrow text-[10px]">Poids soulevé</span>
              <div className="flex items-stretch">
                <input
                  data-testid="input-weight-lifted"
                  type="number"
                  min="0"
                  step="0.5"
                  value={liftWeight}
                  onChange={(e) =>
                    setLiftWeight(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="w-full bg-[#12141a] border border-white/10 rounded-l-sm px-3 py-3 text-xl font-mono font-bold text-white focus:border-[#cd383d] outline-none tabular"
                />
                <span className="flex items-center px-3 bg-[#1f232d] border border-l-0 border-white/10 rounded-r-sm text-xs font-mono text-slate-400 uppercase">
                  {unit}
                </span>
              </div>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="eyebrow text-[10px]">Répétitions</span>
              <input
                data-testid="input-reps-performed"
                type="number"
                min="1"
                max="30"
                step="1"
                value={liftReps}
                onChange={(e) =>
                  setLiftReps(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                className="w-full bg-[#12141a] border border-white/10 rounded-sm px-3 py-3 text-xl font-mono font-bold text-white focus:border-[#cd383d] outline-none tabular"
              />
            </label>
          </div>

          <div>
            <span className="eyebrow text-[10px]">Formule</span>
            <div className="mt-1.5 flex gap-2 flex-wrap">
              {Object.keys(FORMULAS).map((k) => (
                <button
                  key={k}
                  data-testid={`formula-select-${k}`}
                  onClick={() => setFormula(k)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm border transition ${
                    formula === k
                      ? "bg-[#cd383d] border-[#cd383d] text-white"
                      : "border-white/10 text-slate-400 hover:text-white hover:border-white/30"
                  }`}
                >
                  {FORMULAS[k].label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-1 flex items-center justify-between gap-3 border-t border-white/10 pt-4">
            <div>
              <div className="eyebrow text-[10px]">1RM estimé</div>
              <div
                data-testid="estimated-1rm-preview"
                className="font-mono text-3xl font-black text-white tabular"
              >
                {fmt(roundTo(estimated1RM, rounding), rounding ? 0 : 1)}{" "}
                <span className="text-sm text-slate-500 uppercase font-mono">
                  {unit}
                </span>
              </div>
            </div>
            <button
              data-testid="button-calculate-1rm"
              onClick={commitEstimate}
              className="btn-red px-4 py-2.5 rounded-sm flex items-center gap-2 text-sm"
            >
              <ArrowLeftRight size={14} />
              Appliquer au tableau
            </button>
          </div>
        </div>
      )}

      <div className="rounded-sm bg-black/30 border border-white/5 p-3 flex items-start gap-3">
        <Target size={16} className="text-[#cd383d] mt-0.5 shrink-0" />
        <p className="text-xs text-slate-400 leading-relaxed">
          Bascule à tout moment entre saisie directe du{" "}
          <span className="text-white font-semibold">1RM</span> et estimation à
          partir d&apos;un set réel (ex.{" "}
          <span className="text-white font-semibold">245 lbs × 5 reps</span>{" "}
          → 1RM projeté). Le tableau se recalcule en direct.
        </p>
      </div>
    </div>
  );
};
